import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Visible-banner cap. New banners beyond this drop the oldest currently
// visible entry — design says cap-and-drop.
const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 6000;

export const BannerContext = createContext(null);

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return `b-${Date.now()}-${idCounter}`;
};

/**
 * BannerProvider — in-memory queue for the system banner channel (F4.3).
 *
 * Sits as a sibling of ToastProvider — banners are part of the chat region's
 * vertical flow, while toasts live in the global region. The two never share
 * state.
 *
 * showBanner({ kind, text, duration? }) → string id
 * dismissBanner(id) → void
 *
 * Renders no DOM itself: <BannerHost> reads from context and renders the
 * stack into the chat region.
 */
function BannerProvider({ children }) {
  const [banners, setBanners] = useState([]);
  const timersRef = useRef(new Map());

  const clearTimer = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissBanner = useCallback(
    (id) => {
      clearTimer(id);
      setBanners((cur) => cur.filter((b) => b.id !== id));
    },
    [clearTimer],
  );

  const showBanner = useCallback(
    (input) => {
      const kind = input?.kind || 'info';
      const text = input?.text || '';
      const duration =
        typeof input?.duration === 'number' ? input.duration : DEFAULT_DURATION;
      const item = { id: nextId(), kind, text, duration };

      setBanners((cur) => {
        // Cap-and-drop: shed the oldest if we're at the cap.
        const trimmed =
          cur.length >= MAX_VISIBLE ? cur.slice(cur.length - (MAX_VISIBLE - 1)) : cur;
        // Cancel timers for any banners dropped by the trim — they're gone,
        // their auto-dismiss timer should not fire and stomp on something new.
        if (trimmed.length !== cur.length) {
          cur
            .filter((b) => !trimmed.includes(b))
            .forEach((b) => clearTimer(b.id));
        }
        return [...trimmed, item];
      });

      if (duration > 0) {
        const tid = setTimeout(() => {
          timersRef.current.delete(item.id);
          setBanners((cur) => cur.filter((b) => b.id !== item.id));
        }, duration);
        timersRef.current.set(item.id, tid);
      }

      return item.id;
    },
    [clearTimer],
  );

  // Clear any remaining timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((tid) => clearTimeout(tid));
      timers.clear();
    };
  }, []);

  const value = useMemo(
    () => ({ banners, showBanner, dismissBanner }),
    [banners, showBanner, dismissBanner],
  );

  return (
    <BannerContext.Provider value={value}>{children}</BannerContext.Provider>
  );
}

export { MAX_VISIBLE, DEFAULT_DURATION };
export default BannerProvider;
